import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * @typedef {Object} GalleryControlRefs
 * @property {HTMLElement} rail
 * @property {HTMLButtonElement[]} items
 * @property {HTMLElement} position
 */
/** @extends {Component<GalleryControlRefs>} */
class BvPcGalleryControls extends Component {
  requiredRefs = ['rail', 'items', 'position'];
  #controller;
  #desktop = window.matchMedia('(min-width: 750px)');
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#connect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #connect() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    // Quick Add clones product markup into a dialog. Its existing layout stays intact.
    if (this.closest('dialog')) return;
    const slideshow = this.closest('slideshow-component');
    if (!slideshow) return;

    const update = (event) => {
      // A video/model can contain a separate slideshow. Ignore its events.
      if (event && event.target !== slideshow) return;
      if (!this.#desktop.matches) return;
      const index = event?.detail?.index ?? slideshow.current ?? 0;
      this.#update(index, Boolean(event?.detail?.userInitiated));
    };
    slideshow.addEventListener(SlideshowSelectEvent.eventName, update, { signal });
    this.#desktop.addEventListener('change', () => update(), { signal });
    update();
  }

  #update(index, userInitiated) {
    const { rail, items, position } = this.refs;
    if (!Number.isInteger(index) || index < 0 || index >= items.length) return;
    for (const [itemIndex, item] of items.entries()) {
      item.setAttribute('aria-pressed', String(itemIndex === index));
    }
    const selected = items[index];
    position.textContent = selected.getAttribute('aria-label') ?? '';

    // Scroll only the rail. Element.scrollIntoView would move the product page too.
    const railBox = rail.getBoundingClientRect();
    const selectedBox = selected.getBoundingClientRect();
    if (selectedBox.left < railBox.left || selectedBox.right > railBox.right) {
      rail.scrollBy({
        left: selectedBox.left + selectedBox.width / 2 - railBox.left - railBox.width / 2,
        behavior: userInitiated && !this.#reducedMotion.matches ? 'smooth' : 'instant',
      });
    }
  }
}

if (!customElements.get('bv-pc-gallery-controls-component')) {
  customElements.define('bv-pc-gallery-controls-component', BvPcGalleryControls);
}
